import { Box, For, HStack, Text, VStack } from '@chakra-ui/react';
import { useMapContext } from '@components/map/MapContext';
import SensorIcon from '@components/map/popup/infos/sensors/SensorIcon';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { makeSelectPlatformsNamesByCode } from '@redux/config/config.selectors';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { getPlatformsCodesFromMarks } from '@utils/common.utils';
import { getSensorIconKey } from '@utils/map/sensor.utils';
import { findPopupByType } from '@utils/popup.utils';
import { isJamming } from '@utils/sensors/neutral/neutral.utils';
import { getSensorHexColor } from '@utils/sensors/sensors.utils';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { InfoDataTypeEnum, PopupTypeEnum, SensorInfoData } from '@/types/popup.types';
import { NeutralSensorWithSectorsType } from '@/types/sensor/configuration.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';

type Props = {
  monitoringSensor: MonitoringSensorByType<NeutralSensorWithSectorsType>;
  top: number;
  left: number;
};

export default function NeutralInfoCard({ monitoringSensor, top, left }: Readonly<Props>) {
  const { mapId } = useMapContext();
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const sensorPopup = findPopupByType(popups, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_SENSOR);
  const { configuration, status, active, type } = monitoringSensor;
  const isPopupOpen =
    !!sensorPopup && (sensorPopup.data as SensorInfoData).featureValue.sensorUniqueKey === configuration.uniqueKey;
  const platformCodes = getPlatformsCodesFromMarks(configuration.marks);
  const selectPlatformNames = useMemo(makeSelectPlatformsNamesByCode, []);
  const platforms = useAppSelector((state) => selectPlatformNames(state, platformCodes), shallowEqual);

  const isSensorJamming = isJamming(status);

  function handleClick() {
    if (!isPopupOpen) {
      updatePopup({
        mapId,
        popup: {
          type: PopupTypeEnum.INFO,
          position: {
            x: left,
            y: top,
          },
          data: {
            type: InfoDataTypeEnum.INFO_SENSOR,
            featureValue: { sensorUniqueKey: configuration.uniqueKey },
          },
        },
      });
    }
  }

  return (
    <HStack
      gap={2}
      padding={2}
      flexShrink={0}
      cursor={isPopupOpen ? 'default' : 'pointer'}
      backgroundColor={isPopupOpen ? 'neutral.600' : 'neutral.700'}
      _hover={{
        backgroundColor: 'neutral.600',
      }}
      width="100%"
      onClick={() => !isPopupOpen && handleClick()}
    >
      <SensorIcon
        sensorIconName={getSensorIconKey(
          type,
          active,
          configuration.maintenance,
          status?.specificState,
          status?.effectiveStatus,
        )}
      />
      <VStack gap={1} alignItems="start" width="100%">
        <Text
          backgroundColor="neutral.black"
          textStyle="lg"
          paddingX={0.5}
          color={getSensorHexColor({ active, type, maintenance: configuration.maintenance, configuration, status })}
        >
          <FormattedMessage id={`sensors.type.${configuration.type}`} />
        </Text>
        <Tooltip content={configuration.name}>
          <Text
            paddingX={0.5}
            lineClamp={1}
            maxWidth="100%"
            textStyle="lg"
            backgroundColor="neutral.black"
            textTransform="uppercase"
            wordBreak="break-all"
          >
            {configuration.name}
          </Text>
        </Tooltip>
        <HStack maxWidth="100%" gap={0.5}>
          <For each={platforms.slice(0, 5)}>
            {(platform) => (
              <Tooltip key={platform} content={platform}>
                <Text
                  paddingX={0.25}
                  backgroundColor="neutral.800"
                  textTransform="uppercase"
                  lineHeight="18px"
                  color="neutral.white"
                >
                  {platform.slice(0, 4)}
                </Text>
              </Tooltip>
            )}
          </For>
        </HStack>
      </VStack>
      {isSensorJamming && (
        <Box
          width="32px"
          height="32px"
          flexShrink={0}
          backgroundColor="red.900"
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundImage="url(/jamming-icon.gif)"
        />
      )}
    </HStack>
  );
}
